/**
 * @component ry-radio
 * @category  form
 * @summary   Vanilla single-select radio button. Wraps a hidden `<input type="radio">`. Sharing `[name]` across radios forms a group — only one can be checked. Pair with `<ry-radio-group>` for arrow-key navigation and a labelled `role="radiogroup"` wrapper.
 *
 * @attribute checked   boolean   default=false   Initial checked state.
 * @attribute disabled  boolean   default=false   Non-interactive.
 * @attribute name      string?                   Form field name. REQUIRED for radio-group behavior — radios with the same name auto-uncheck siblings.
 * @attribute value     string?                   Submitted value when checked.
 *
 * @property  checked   boolean   Mirrors `checked` attribute (get/set).
 * @property  value     string    The inner input's value.
 *
 * @event ry:beforechange
 *        detail: { value: string, checked: true, previous?: string }
 *        fires:  from the inner input's `click` (mouse, touch, Space, label click) before the radio checks — only when it is not already checked. `previous` is the value of the group's currently-checked `<ry-radio>` (omitted when none / no `[name]`).
 *        cancelable: preventDefault() cancels the inner input's click — the platform leaves this radio unchecked, the group's current selection stands, and `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value: string, checked: boolean }
 *        fires:  user toggle (mouse, touch, Space). Programmatic `.checked = true` is SILENT.
 *
 * @keyboard Space — checks when focused. (Native input behavior. Arrow keys are
 *          handled by `<ry-radio-group>` if present.)
 *
 * @content  Inline text becomes the visible label next to the dot.
 *
 * @dom
 *   ry-radio
 *     > label.ry-radio__wrapper
 *       > input.ry-radio__input        (visually hidden, type="radio")
 *       > span.ry-radio__dot           (visual circle + inner dot)
 *       > span.ry-radio__label         (only if textContent provided)
 *
 * @gotcha The visually-hidden input is `position: absolute`. The host element is
 *         `position: relative` (set in ry-structure.css) so the input anchors here.
 *
 * @gotcha Setting `.checked` programmatically updates state SILENTLY — it does NOT
 *         fire `ry:change`. Mirror of the ry-switch contract.
 *
 * @gotcha `name` is REQUIRED for radio-group behavior. Without it, radios are
 *         independent and clicking one does NOT uncheck others.
 *
 * @example simple
 * ```html
 * <ry-radio name="size" value="sm">Small</ry-radio>
 * <ry-radio name="size" value="md" checked>Medium</ry-radio>
 * <ry-radio name="size" value="lg">Large</ry-radio>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-radio name="plan" value="free" checked>Free</ry-radio>
 * <ry-radio name="plan" value="pro">Pro</ry-radio>
 * <ry-radio name="plan" value="ent" disabled>Enterprise (contact sales)</ry-radio>
 * ```
 *
 * @example no-label
 * ```html
 * <ry-radio name="x" value="a"></ry-radio>
 * ```
 *
 * @tutorial basic-toggle
 * ```js
 * const el = mount('<ry-radio name="x" value="a">A</ry-radio>');
 * await expectEqual(el.checked, false, 'starts unchecked');
 *
 * const input = el.querySelector('input');
 * input.checked = true;
 * input.dispatchEvent(new Event('change', { bubbles: true }));
 * await expectEqual(el.checked, true, 'after user toggle');
 * await expectAttr(el, 'checked', '');
 * ```
 *
 * @tutorial programmatic-setter
 * ```js
 * // Setter mutates state but does NOT fire ry:change.
 * const el = mount('<ry-radio name="x" value="a">A</ry-radio>');
 * let fired = 0;
 * el.addEventListener('ry:change', () => fired++);
 *
 * el.checked = true;
 * await expectEqual(el.checked, true);
 * await expectAttr(el, 'checked', '');
 * await expectEqual(fired, 0, 'setter does NOT fire ry:change');
 * ```
 *
 * @tutorial same-name-siblings-auto-uncheck
 * ```js
 * // Native radio uncheck the inner input, ry-radio syncs the host [checked].
 * const wrap = mount(`
 *   <div>
 *     <ry-radio id="a" name="grp" value="a" checked>A</ry-radio>
 *     <ry-radio id="b" name="grp" value="b">B</ry-radio>
 *   </div>
 * `);
 * const a = wrap.querySelector('#a');
 * const b = wrap.querySelector('#b');
 * const bInput = b.querySelector('input');
 * bInput.checked = true;
 * bInput.dispatchEvent(new Event('change', { bubbles: true }));
 * await expectEqual(a.hasAttribute('checked'), false, 'A unchecked');
 * await expectAttr(b, 'checked', '', 'B checked');
 * ```
 *
 * @tutorial disabled-blocks
 * ```js
 * const el = mount('<ry-radio name="x" value="a" disabled>X</ry-radio>');
 * await expectAttr(el, 'disabled', '');
 * const input = el.querySelector('input');
 * await expectEqual(input.disabled, true, 'inner input is also disabled');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange cancels the inner input's click —
 * // the radio stays unchecked, the group keeps its selection, ry:change is silent.
 * const wrap = mount(`
 *   <div>
 *     <ry-radio id="a" name="grp" value="a" checked>A</ry-radio>
 *     <ry-radio id="b" name="grp" value="b">B</ry-radio>
 *   </div>
 * `);
 * const a = wrap.querySelector('#a');
 * const b = wrap.querySelector('#b');
 * const bInput = b.querySelector('input');
 * const changed = [];
 * b.addEventListener('ry:change', () => changed.push(1));
 * let seen = null;
 * b.addEventListener('ry:beforechange', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * bInput.click();
 * await expectEqual(seen.previous, 'a', 'detail.previous is the current group value');
 * await expectEqual(b.hasAttribute('checked'), false, 'vetoed: B stays unchecked');
 * await expectAttr(a, 'checked', '', 'vetoed: A stays the selection');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * bInput.click();
 * await expectAttr(b, 'checked', '', 'without the veto, B is selected');
 * await expectEqual(a.hasAttribute('checked'), false, 'and A is unchecked');
 * await expectEqual(changed.length, 1);
 * ```
 *
 * @see ry-radio-group, ry-checkbox, ry-tile-radio
 */

import { RyElement } from '../core/ry-element.js';

let radioIdCounter = 0;

interface RadioChangeDetail {
  value: string;
  checked: boolean;
}

interface RadioBeforeChangeDetail {
  value: string;
  checked: true;
  previous?: string;
}

export class RyRadio extends RyElement {
  #input: HTMLInputElement | null = null;

  static observedAttributes = ['checked', 'disabled'] as const;

  setup(): void {
    this.#transformMarkup();

    this.#input = this.$<HTMLInputElement>('input[type="radio"]');
    if (this.#input) {
      this.on(this.#input, 'click', this.#handleClick);
      this.on(this.#input, 'change', this.#handleChange);
    }
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="dot"]')) return;

    const id = `ry-radio-${++radioIdCounter}`;
    const name = this.getAttribute('name') ?? '';
    const value = this.getAttribute('value') ?? '';
    const checked = this.hasAttribute('checked');
    const disabled = this.hasAttribute('disabled');
    const label = this.textContent?.trim() ?? '';

    this.innerHTML = `
      <label data-ry-target="wrapper" class="ry-radio__wrapper" for="${id}">
        <input
          type="radio"
          id="${id}"
          data-ry-target="input"
          class="ry-radio__input"
          ${name ? `name="${name}"` : ''}
          ${value ? `value="${value}"` : ''}
          ${checked ? 'checked' : ''}
          ${disabled ? 'disabled' : ''}
          aria-checked="${checked}"
        >
        <span data-ry-target="dot" class="ry-radio__dot" aria-hidden="true"></span>
        ${label ? `<span data-ry-target="label" class="ry-radio__label">${label}</span>` : ''}
      </label>
    `;
  }

  // The interception point. A radio's `click` pre-activation has ALREADY
  // checked this input (and unchecked the native siblings) by the time the
  // event dispatches; cancelling the click makes the platform restore the
  // group and no native `change` fires. The host `[checked]` attributes are
  // still the committed state here (they only sync in #handleChange), so the
  // group's previous selection is read from them.
  #handleClick = (event: MouseEvent): void => {
    if (!this.#input) return;
    const previous = this.#checkedSibling();
    // Clicking the already-selected radio is a no-op — no change, no interception.
    if (previous === this || (!previous && this.hasAttribute('checked'))) return;
    const detail: RadioBeforeChangeDetail = { value: this.#input.value, checked: true };
    if (previous) detail.previous = previous.value;
    if (!this.emit<RadioBeforeChangeDetail>('beforechange', detail)) event.preventDefault();
  };

  /** The `<ry-radio>` in this group whose host `[checked]` is set (committed state), if any. */
  #checkedSibling(): RyRadio | null {
    const name = this.#input?.name;
    if (!name) return null;
    const root = this.getRootNode() as Document | ShadowRoot;
    return root.querySelector<RyRadio>(`ry-radio[name="${name}"][checked]`);
  }

  #handleChange = (): void => {
    if (!this.#input) return;

    const checked = this.#input.checked;
    this.#input.setAttribute('aria-checked', String(checked));

    if (checked) this.setAttribute('checked', '');
    else this.removeAttribute('checked');

    this.emit<RadioChangeDetail>('change', {
      value: this.#input.value,
      checked,
    });

    // Native radios with the same `name` auto-uncheck siblings, but the host
    // attribute won't reflect that. Sync sibling attributes so CSS / querying
    // by [checked] stays accurate.
    if (checked && this.#input.name) {
      const root = this.getRootNode() as Document | ShadowRoot;
      root.querySelectorAll<RyRadio>(
        `ry-radio[name="${this.#input.name}"]`
      ).forEach(el => {
        if (el !== this) el.removeAttribute('checked');
      });
    }
  };

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.#input) return;

    if (name === 'checked') {
      const on = newValue !== null;
      this.#input.checked = on;
      this.#input.setAttribute('aria-checked', String(on));
    } else if (name === 'disabled') {
      this.#input.disabled = newValue !== null;
    }
  }

  get checked(): boolean {
    return this.#input?.checked ?? false;
  }

  set checked(value: boolean) {
    if (!this.#input) return;
    this.#input.checked = value;
    this.#input.setAttribute('aria-checked', String(value));
    if (value) this.setAttribute('checked', '');
    else this.removeAttribute('checked');
  }

  get value(): string {
    return this.#input?.value ?? '';
  }

  set value(val: string) {
    if (this.#input) this.#input.value = val;
  }
}

customElements.define('ry-radio', RyRadio);
